import { put, call, takeEvery, all } from 'redux-saga/effects'
import { GQL } from '../services'
import {
  REACTIVATE_SUBSCRIPTION_INIT,
  REACTIVATE_SUBSCRIPTION_SUCCESS,
  REACTIVATE_SUBSCRIPTION_FAILURE
} from '../actions/types'
import { pixel } from 'src/utils/pixel'
import { gtm } from 'src/utils/googleTagManager'
import { trackGrossResurrectionEvent } from 'src/utils/tracking'
import { RESURRECTION_COUPON_CODES } from 'src/constants/resurrection-constants'
import {
  showReactivationSuccessMessage,
  showPrepaidOfferSuccessMessage,
  showPrepaidOfferErrorMessage,
  showCouponErrorMessage,
  showCartGenerationErrorMessage,
  showReactivationErrorMessage,
  prepareResurrectionCampaign,
  ResurrectionCampaign,
  handleReactivationRedirect
} from 'src/utils/resurrection-utils'
import { reloadSubscriptionData } from 'src/utils/subscription-reload'

// Define a type alias for optional string values
type OptionalString = string | null | undefined

interface SubscriptionData {
  publicCreateSubscriptionEvent?: {
    userId: string
    isReactivation: boolean
    resurrectionCount: number
    lastResurrectionDate: string
    email: string | null
  }
}

interface ChefCartResponse {
  generateCartFromResurrectionCampaign?: {
    deliveryDate: string
    chefIds: string[]
  }
}

interface PrepaidOfferResponse {
  assignPrepaidOffer?: {
    status: boolean
  }
}

interface ResurrectionTrackingData {
  resurrectionCampaign: ResurrectionCampaign | null
  couponCode: string | null
  prepaidOfferId: string | null
  creditsAvailable?: number | null
  subscriptionData: {
    userId: string
    isReactivation: boolean
    resurrectionCount: number
    lastResurrectionDate: string
    email: string | null
  } | null
}

/**
 * Saga to handle subscription reactivation
 * Processes resurrection campaigns, coupons, and prepaid offers
 * @param action Redux action with reactivation payload
 */
export function* reactivateSubscription(action: any): any {
  const { payload } = action
  const {
    creditsAvailable,
    prepaidOfferId: prepaidOffer,
    resurrectionCampaign: campaignFromPayload,
    coupon: couponFromPayload
  } = payload

  try {
    // Process resurrection campaign data if present
    const resurrectionCampaign = prepareResurrectionCampaign(
      campaignFromPayload
    )
    // Use the coupon from the processed campaign object OR from the payload directly
    const couponToApply = resurrectionCampaign?.couponCode ?? couponFromPayload

    // Call reactivateSubscription GraphQL operation (proceed even if resurrectionCampaign is null/undefined)
    const subscriptionData: SubscriptionData = yield call(
      GQL.reactivateSubscription as any,
      resurrectionCampaign
    )

    const urlParams = new URLSearchParams(window.location.search)
    const mealIds = urlParams.get('mealIds')

    // Special case: RES-35X4 is the credits-based resurrection coupon
    // Use couponToApply here instead of couponCode from props
    const isResurrected =
      couponToApply !== RESURRECTION_COUPON_CODES.CREDITS_35X4

    // Only handle coupon application if present
    if (couponToApply) {
      yield* handleCouponApplication(couponToApply, isResurrected)
    }

    // Only handle prepaid offer if present
    if (prepaidOffer) {
      yield* handlePrepaidOffer(prepaidOffer)
    }

    // Always handle cart generation (order placement)

    const {
      redirectToDeliveryDate,
      chefIds,
      isOrganic
    } = yield* handleCartGeneration(resurrectionCampaign, urlParams)

    // Track events
    trackReactivationEvents(resurrectionCampaign)

    // Prepare resurrection tracking data
    const resurrectionData = prepareResurrectionTrackingData(
      subscriptionData,
      resurrectionCampaign,
      prepaidOffer,
      creditsAvailable
    )

    // Track resurrection event
    trackGrossResurrectionEvent(resurrectionData as any)

    // Reload subscription data to ensure UI consistency before dispatching success
    // This is crucial for both organic and non-organic users
    yield call(reloadSubscriptionData)

    // Don't show toast for organic users - OrderSummaryGutter will handle it
    if (!isOrganic) {
      // Only show toast for campaign-based users
      showReactivationSuccessMessage()
      // Call handleReactivationRedirect for non-organic cases
      yield call(
        handleReactivationRedirect,
        payload.history,
        redirectToDeliveryDate,
        {
          chefIds, // from handleCartGeneration
          mealIds // from urlParams.get('mealIds') earlier in the saga
        }
      )
    } else {
      console.log(
        '[REACTIVATION_DEBUG] Organic user - skipping automatic navigation, OrderSummaryGutter will handle order placement'
      )
    }

    try {
      // Explicitly log the action being dispatched
      const successAction = {
        type: REACTIVATE_SUBSCRIPTION_SUCCESS,
        payload: {
          subscriptionData,
          redirectToDeliveryDate,
          chefIds,
          mealIds,
          isOrganic // Include flag to indicate if this was an organic reactivation
        }
      }

      yield put(successAction)
    } catch (dispatchError) {
      // Try a simpler action as fallback
      const fallbackAction = {
        type: REACTIVATE_SUBSCRIPTION_SUCCESS,
        payload: { isOrganic }
      }
      console.log(
        '[REACTIVATION_DEBUG] Dispatching fallback success action:',
        fallbackAction
      )
      yield put(fallbackAction)
    }
  } catch (error) {
    // Handle general reactivation failure
    console.error('[REACTIVATION_DEBUG] Reactivation failed with error:', error)
    showReactivationErrorMessage(error)

    console.log('[reactivateSubscription] Dispatching FAILURE action.')
    yield put({
      type: REACTIVATE_SUBSCRIPTION_FAILURE,
      error
    })
  }
}

/**
 * Handle coupon application
 * Validates and applies coupon codes to the user's subscription
 */
function* handleCouponApplication(
  couponCode: OptionalString,
  isResurrected: boolean
) {
  if (!couponCode) return

  // Basic validation to ensure coupon code is not empty after trimming
  const trimmedCoupon = couponCode.trim()
  if (!trimmedCoupon) {
    return
  }

  try {
    yield call(GQL.consumeCoupon as any, trimmedCoupon, isResurrected)
  } catch (error) {
    console.error('Error applying coupon:', error)
    showCouponErrorMessage(trimmedCoupon)
  }
}

/**
 * Handle prepaid offer assignment
 */
function* handlePrepaidOffer(prepaidOffer: OptionalString) {
  if (!prepaidOffer) return {}

  try {
    const prepaidResponse: PrepaidOfferResponse = yield call(
      GQL.assignPrepaidOffer as any,
      prepaidOffer
    )

    if (prepaidResponse?.assignPrepaidOffer?.status) {
      showPrepaidOfferSuccessMessage()
    } else {
      showPrepaidOfferErrorMessage()
    }

    return prepaidResponse
  } catch (error) {
    console.error('Error assigning prepaid offer:', error)
    showPrepaidOfferErrorMessage()
    return {}
  }
}

/**
 * Handle cart generation for resurrection campaign or organic users
 */
function* handleCartGeneration(
  resurrectionCampaign: ResurrectionCampaign | null,
  urlParams: URLSearchParams
) {
  console.log(
    '[REACTIVATION_DEBUG] handleCartGeneration called with campaign:',
    resurrectionCampaign
  )

  // For organic users (no resurrection campaign)
  if (!resurrectionCampaign?.id) {
    // For organic users, we don't need to generate a cart from a campaign
    // The order will be placed through the normal flow via the onConfirm() prop
    // in OrderSummaryGutter.jsx after the reactivation succeeds
    return {
      redirectToDeliveryDate: undefined,
      chefIds: undefined,
      isOrganic: true // Flag to indicate this was an organic reactivation
    }
  }

  console.log(
    '[REACTIVATION_DEBUG] Campaign found - processing non-organic user with campaign ID:',
    resurrectionCampaign.id
  )
  // For users with a resurrection campaign
  try {
    const chefCartResponse: ChefCartResponse = yield call(
      GQL.generateCartFromResurrectionCampaign as any,
      {
        campaignSlug: resurrectionCampaign.id,
        chefId: resurrectionCampaign.chefId,
        chefBrandId: resurrectionCampaign.chefBrandId,
        chefBrandIds: resurrectionCampaign.chefBrandIds,
        highlightedMealsIds: resurrectionCampaign.highlightedMealsIds,
        noPopulateCart: urlParams.get('npc') === '1'
      }
    )

    return {
      redirectToDeliveryDate:
        chefCartResponse?.generateCartFromResurrectionCampaign?.deliveryDate,
      chefIds: chefCartResponse?.generateCartFromResurrectionCampaign?.chefIds,
      isOrganic: false
    }
  } catch (error) {
    console.error(
      '[REACTIVATION_DEBUG] Error generating cart for campaign user:',
      error
    )
    showCartGenerationErrorMessage()
    return {
      redirectToDeliveryDate: undefined,
      chefIds: undefined,
      isOrganic: false
    }
  }
}

/**
 * Track reactivation events
 */
function trackReactivationEvents(
  resurrectionCampaign: ResurrectionCampaign | null
) {
  try {
    const couponToUse = resurrectionCampaign?.couponCode
    const campaignId = resurrectionCampaign?.id

    gtm.trackEvent({
      name: 'conversion', // Or appropriate event name
      data: {
        event_category: 'subscription',
        event_action: 'resubscribe', // Or specific resurrection event
        campaign_id: campaignId,
        coupon_code: couponToUse
      }
    })
  } catch (e) {
    console.warn('Pixel trackPageView error:', e)

    pixel.trackPageView()
  }
}

/**
 * Prepare resurrection tracking data
 */
function prepareResurrectionTrackingData(
  subscriptionData: SubscriptionData,
  resurrectionCampaign: ResurrectionCampaign | null,
  prepaidOfferId: OptionalString,
  creditsAvailable?: number | null
): ResurrectionTrackingData {
  const couponToUse = resurrectionCampaign?.couponCode
  const processedSubscriptionData = subscriptionData?.publicCreateSubscriptionEvent
    ? {
        userId: subscriptionData.publicCreateSubscriptionEvent.userId,
        isReactivation:
          subscriptionData.publicCreateSubscriptionEvent.isReactivation,
        resurrectionCount:
          subscriptionData.publicCreateSubscriptionEvent.resurrectionCount,
        lastResurrectionDate:
          subscriptionData.publicCreateSubscriptionEvent.lastResurrectionDate,
        email: subscriptionData.publicCreateSubscriptionEvent.email
      }
    : null

  return {
    resurrectionCampaign,
    couponCode: couponToUse ?? null,
    prepaidOfferId:
      resurrectionCampaign?.prepaidCampaign ?? prepaidOfferId ?? null,
    creditsAvailable,
    subscriptionData: processedSubscriptionData
  }
}

export default function* rootInitialSaga(): any {
  yield all([
    takeEvery(
      REACTIVATE_SUBSCRIPTION_INIT as any,
      reactivateSubscription as any
    )
  ])
}
